import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, RefreshCw, Loader2, TrendingUp, Users, Mail,
  MousePointerClick, Clock, CheckCircle, AlertCircle,
  Send, PauseCircle, AlertTriangle, UserMinus,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { interspireApi } from '../services/api';
import { DashboardSummary } from '../types';
import { GUEST_TOKEN_KEY, useAuth } from '../context/AuthContext';
import SmtpComBounceChart from '../components/SmtpComBounceChart';
import UnsubscribeTrendChart from '../components/UnsubscribeTrendChart';
import {
  DASHBOARD_CHART_RANGE_OPTIONS,
  DashboardChartRange,
  readStoredDashboardChartRange,
  storeDashboardChartRange,
} from '../lib/dashboardChartRange';

function formatUnixDate(ts: number | null): string {
  if (ts == null || ts === 0) return '—';
  return new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPct(val: number | null): string {
  if (val == null) return '—';
  return `${val.toFixed(1)}%`;
}

function formatNum(val: number): string {
  return val.toLocaleString();
}

// Color classes for rate values
function openRateColor(rate: number | null): string {
  if (rate == null) return 'text-gray-400 dark:text-slate-500';
  if (rate >= 20) return 'text-green-600 dark:text-green-400';
  if (rate >= 12) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function clickRateColor(rate: number | null): string {
  if (rate == null) return 'text-gray-400 dark:text-slate-500';
  if (rate >= 3) return 'text-green-600 dark:text-green-400';
  if (rate >= 1) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function bounceRateColor(rate: number | null): string {
  if (rate == null) return 'text-gray-400 dark:text-slate-500';
  if (rate < 2) return 'text-green-600 dark:text-green-400';
  if (rate < 5) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

function unsubRateColor(rate: number | null): string {
  if (rate == null) return 'text-gray-400 dark:text-slate-500';
  if (rate < 0.5) return 'text-green-600 dark:text-green-400';
  if (rate < 1.5) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}
function KpiCard({ icon, label, value, sub }: KpiCardProps) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-slate-400">{sub}</div>}
    </div>
  );
}

const DAY_OPTIONS = [30, 60, 90] as const;
type DayOption = typeof DAY_OPTIONS[number];

export default function Dashboard() {
  const { canAccess } = useAuth();
  const [days, setDays] = useState<DayOption>(30);
  const [chartRange, setChartRange] = useState<DashboardChartRange>(() =>
    readStoredDashboardChartRange()
  );
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const guestToken = sessionStorage.getItem(GUEST_TOKEN_KEY);

  const load = useCallback(async (d: DayOption) => {
    setLoading(true);
    setError(null);
    try {
      const res = await interspireApi.getDashboard(d);
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const { aggregate, recentCampaigns, queueSummary } = data ?? {
    aggregate: null, recentCampaigns: [], queueSummary: null,
  };

  const queueReviewEnabled = canAccess('queueReviewModal');
  const sendsEnabled = canAccess('emailSends');
  const queueEnabled = canAccess('emailQueue');

  function sendsLink(statid: number) {
    if (!sendsEnabled) return null;
    return guestToken
      ? `/sends/${statid}?gt=${encodeURIComponent(guestToken)}`
      : `/sends/${statid}`;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-white">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-indigo-500" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <div className="flex rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden text-sm">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 transition-colors ${
                    days === d
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              onClick={() => load(days)}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-4">
          <KpiCard
            icon={<Send className="h-3.5 w-3.5" />}
            label="Sends"
            value={loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : formatNum(aggregate?.totalSends ?? 0)}
            sub={`Last ${days} days`}
          />
          <KpiCard
            icon={<Users className="h-3.5 w-3.5" />}
            label="Recipients"
            value={loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : formatNum(aggregate?.totalRecipients ?? 0)}
            sub="Emails delivered"
          />
          <KpiCard
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            label="Avg Open Rate"
            value={
              loading
                ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                : <span className={openRateColor(aggregate?.avgOpenRate ?? null)}>{formatPct(aggregate?.avgOpenRate ?? null)}</span>
            }
            sub={aggregate?.avgOpenRate == null && !loading ? 'Not tracked' : undefined}
          />
          <KpiCard
            icon={<MousePointerClick className="h-3.5 w-3.5" />}
            label="Avg Click Rate"
            value={
              loading
                ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                : <span className={clickRateColor(aggregate?.avgClickRate ?? null)}>{formatPct(aggregate?.avgClickRate ?? null)}</span>
            }
            sub={aggregate?.avgClickRate == null && !loading ? 'Not tracked' : undefined}
          />
          <KpiCard
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
            label="Avg Bounce Rate"
            value={
              loading
                ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                : <span className={bounceRateColor(aggregate?.avgBounceRate ?? null)}>{formatPct(aggregate?.avgBounceRate ?? null)}</span>
            }
            sub={`Last ${days} days`}
          />
          <KpiCard
            icon={<UserMinus className="h-3.5 w-3.5" />}
            label="Avg Unsub Rate"
            value={
              loading
                ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                : <span className={unsubRateColor(aggregate?.avgUnsubRate ?? null)}>{formatPct(aggregate?.avgUnsubRate ?? null)}</span>
            }
            sub={`Last ${days} days`}
          />
          <KpiCard
            icon={<Clock className="h-3.5 w-3.5" />}
            label="Queued"
            value={loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : formatNum(queueSummary?.total ?? 0)}
            sub={
              queueSummary
                ? `${queueSummary.scheduled} scheduled · ${queueSummary.paused} paused`
                : undefined
            }
          />
          <KpiCard
            icon={<Mail className="h-3.5 w-3.5" />}
            label="Email Review"
            value={loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : (
              <span className="text-base font-semibold space-x-1.5">
                <span className="text-green-600 dark:text-green-400">{queueSummary?.aiPassed ?? 0}✓</span>
                <span className="text-red-500">{queueSummary?.aiFailed ?? 0}!</span>
              </span>
            )}
            sub={queueSummary ? `${queueSummary.aiNotRun} not run · ${queueSummary.aiOverridden} overridden` : undefined}
          />
        </div>

        {/* Recent Campaign Performance */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Campaign Performance</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Last 10 sends in the past {days} days</p>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
          ) : recentCampaigns.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400 dark:text-slate-500">
              No sends found in the last {days} days.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide border-b border-gray-100 dark:border-slate-700">
                    <th className="text-left px-4 py-2.5">Campaign</th>
                    <th className="text-left px-4 py-2.5 whitespace-nowrap">Send Date</th>
                    <th className="text-right px-4 py-2.5 whitespace-nowrap">Recipients</th>
                    <th className="text-right px-4 py-2.5 whitespace-nowrap">Open %</th>
                    <th className="text-right px-4 py-2.5 whitespace-nowrap">Click %</th>
                    <th className="text-right px-4 py-2.5 whitespace-nowrap">Bounce %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                  {recentCampaigns.map((c) => {
                    const link = sendsLink(c.statid);
                    return (
                      <tr key={c.statid} className="hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-2.5 max-w-xs">
                          {link ? (
                            <a href={link} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline truncate block">
                              {c.campaignName}
                            </a>
                          ) : (
                            <span className="truncate block text-gray-700 dark:text-slate-300">{c.campaignName}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 dark:text-slate-400 whitespace-nowrap">
                          {formatUnixDate(c.startDate)}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-700 dark:text-slate-300 tabular-nums">
                          {formatNum(c.recipientsCount)}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${openRateColor(c.openRate)}`}>
                          {formatPct(c.openRate)}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${clickRateColor(c.clickRate)}`}>
                          {formatPct(c.clickRate)}
                        </td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-medium ${bounceRateColor(c.bounceRate)}`}>
                          {formatPct(c.bounceRate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Shared lookback for SMTP.com + Interspire charts */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Chart range</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              Applies to SMTP.com bounce rate and Interspire unsubscribes below
            </p>
          </div>
          <div
            className="inline-flex flex-wrap rounded-md border border-gray-200 dark:border-slate-600 p-0.5 text-xs"
            role="group"
            aria-label="Dashboard chart lookback"
          >
            {DASHBOARD_CHART_RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setChartRange(opt.value);
                  storeDashboardChartRange(opt.value);
                }}
                className={`px-2.5 py-1.5 rounded ${
                  chartRange === opt.value
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                }`}
                title={opt.label}
              >
                {opt.shortLabel}
              </button>
            ))}
          </div>
        </div>

        {/* SMTP.com bounce rate */}
        <SmtpComBounceChart range={chartRange} />

        {/* Interspire unsubscribes */}
        <UnsubscribeTrendChart range={chartRange} />

        {/* Queue Pipeline */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Queue Pipeline</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Emails currently scheduled or paused</p>
            </div>
            {queueEnabled && (
              <Link
                to={guestToken ? `/queue?gt=${encodeURIComponent(guestToken)}` : '/queue'}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                View all →
              </Link>
            )}
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
          ) : !data ? null : (
            <QueuePipelineTable
              queueSummary={queueSummary!}
              loading={loading}
              queueReviewEnabled={queueReviewEnabled}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Queue pipeline summary cards ─────────────────────────────────────────────
function QueuePipelineTable({
  queueSummary,
  queueReviewEnabled,
}: {
  queueSummary: DashboardSummary['queueSummary'];
  loading: boolean;
  queueReviewEnabled: boolean;
}) {
  const total = queueSummary.total;
  if (total === 0) {
    return (
      <div className="py-12 text-center text-sm text-gray-400 dark:text-slate-500">
        No emails currently queued.
      </div>
    );
  }
  return (
    <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <QueueStatCard
        icon={<Clock className="h-4 w-4 text-blue-500" />}
        label="Scheduled"
        value={queueSummary.scheduled}
        total={total}
      />
      <QueueStatCard
        icon={<PauseCircle className="h-4 w-4 text-yellow-500" />}
        label="Paused"
        value={queueSummary.paused}
        total={total}
      />
      {queueReviewEnabled && (
        <>
          <QueueStatCard
            icon={<CheckCircle className="h-4 w-4 text-green-500" />}
            label="Review Passed"
            value={queueSummary.aiPassed + queueSummary.aiOverridden}
            total={total}
            sub={queueSummary.aiOverridden > 0 ? `${queueSummary.aiOverridden} overridden` : undefined}
          />
          <QueueStatCard
            icon={<AlertCircle className="h-4 w-4 text-red-500" />}
            label="Review Flagged"
            value={queueSummary.aiFailed}
            total={total}
            sub={queueSummary.aiNotRun > 0 ? `${queueSummary.aiNotRun} not run` : undefined}
          />
        </>
      )}
    </div>
  );
}

function QueueStatCard({
  icon, label, value, total, sub,
}: { icon: React.ReactNode; label: string; value: number; total: number; sub?: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="bg-gray-50 dark:bg-slate-700/40 rounded-lg p-3 flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold text-gray-900 dark:text-white">{value.toLocaleString()}</div>
      <div className="text-xs text-gray-400 dark:text-slate-500">
        {pct}% of queue{sub ? ` · ${sub}` : ''}
      </div>
    </div>
  );
}
